import React, { useCallback, useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GrAttachment } from "react-icons/gr";
import { RxCross2 } from "react-icons/rx";
import { MdError } from "react-icons/md";
import { v4 as uuidv4 } from "uuid";
import { _allExtensions, _formatFileSize } from "../../../utils/common";
import toast from "react-hot-toast";
import {
  initializeUpload,
  selectAllUploads,
  UPLOAD_STATUS,
} from "../../../store/slices/fileUpload.slice";
import uploadService from "../../../services/fileUpload/uploadService";
import { MASTER_API_URL } from "../../../configs/constants";

const ReduxFileUploader = ({
  label = "",
  placeholder = "Choose file",
  labelClass = "",
  required = false,
  id = "",
  limit = 5,
  onChange = () => null,
  error = false,
  accept = [],
  hidden = false,
  info = "JPG, PNG, or JPEG formats, max size is 100 MB each",
  multiple = true,
  value = [],
  folder = "",
  component = false,
  loading = false,
  // Agar true hai to upload VITE_MASTER_API_URL par jayega,
  // warna default VITE_API_URL (through relative /service/upload-chunk)
  useMasterApi = false,
}) => {
  const dispatch = useDispatch();
  const uploads = useSelector(selectAllUploads);
  // Ensure internal state is always an array
  const [files, setFiles] = useState(
    Array.isArray(value) ? value : value ? [value] : []
  );
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Update files when value prop changes (for external control)
  useEffect(() => {
    const normalizedValue = Array.isArray(value) ? value : value ? [value] : [];
    setFiles(normalizedValue);
  }, [value]);

  // Update files when uploads complete, fail, or are in progress
  useEffect(() => {
    // Update file state based on upload status changes
    setFiles((prevFiles) => {
      const currentFiles = Array.isArray(prevFiles) ? prevFiles : [];
      let hasChanges = false;
      const updatedFiles = currentFiles.map((file) => {
        const upload = uploads[file.uuid];
        if (upload) {
          // Check if there are any changes before updating
          if (
            file.loading !== (upload.status !== UPLOAD_STATUS.COMPLETED && upload.status !== UPLOAD_STATUS.FAILED) ||
            file.progress !== upload.progress ||
            file.error !== (upload.status === UPLOAD_STATUS.FAILED)
          ) {
            hasChanges = true;
            return {
              ...file,
              loading: upload.status !== UPLOAD_STATUS.COMPLETED && upload.status !== UPLOAD_STATUS.FAILED,
              progress: upload.progress || 0,
              error: upload.status === UPLOAD_STATUS.FAILED,
              failed: upload.status === UPLOAD_STATUS.FAILED,
              path: upload.path || file.path,
              uploadUrl: upload.path || file.uploadUrl,
              serverResponse: upload,
            };
          }
        }
        return file;
      });

      // Only trigger onChange if there are actual changes
      if (hasChanges) {
        onChange(updatedFiles);
      }
      
      return hasChanges ? updatedFiles : prevFiles;
    });
  }, [uploads, onChange]);

  const _formatFiles = (files) => {
    return Array.from(files).map((file) => ({
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      localpreview: URL.createObjectURL(file),
      loading: true,
      uuid: uuidv4(),
      _fileObject: file, // Store file object for upload
      info: "",
      accept: "",
    }));
  };

  const handleFileChange = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const localFiles = _formatFiles(e.target.files);

      // Validate files
      const validFiles = localFiles.filter((file) => {
        const extension = file.name.split(".").pop().toLowerCase();
        const isValidExtension = _allExtensions.includes(extension);
        const isAcceptedFormat = !accept.length || accept.includes(extension);

        if (!isValidExtension || !isAcceptedFormat) {
          file.loading = false;
          file.error = true;
          return false;
        }
        return true;
      });

      const invalidFiles = localFiles.filter((file) => file.error);

      // Calculate new total based on whether multiple files are allowed
      const existingCount = multiple ? files.length : 0;
      if (existingCount + validFiles.length > limit) {
        toast.error(`You can only upload up to ${limit} files.`);
        return;
      }

      if (!validFiles?.length) {
        if (accept?.length) {
          toast.error(
            `Please check the file format! Only the ${accept.join(
              ", "
            )} formats are allowed.`
          );
        } else {
          toast.error("Please check the file format!");
        }
        return;
      }

      // Add/replace files in local state immediately
      const newFiles = multiple
        ? [...files, ...validFiles]
        : validFiles.slice(0, limit); // single-file mode: replace old with new
      setFiles(newFiles);
      onChange(newFiles);

      // Start upload process for each file
      // Decide upload endpoint for this batch
      const relativeEndpoint = "/service/upload-chunk";
      const uploadEndpoint =
        useMasterApi && MASTER_API_URL
          ? `${MASTER_API_URL.replace(/\/+$/, "")}${relativeEndpoint}`
          : relativeEndpoint;

      validFiles.forEach((fileData) => {
        if (fileData._fileObject) {
          try {
            const fileId = fileData.uuid;

            // Initialize upload in Redux
            dispatch(
              initializeUpload({
                file: {
                  name: fileData.name,
                  size: fileData.size,
                  type: fileData.type,
                  lastModified: fileData.lastModified,
                },
                totalChunks: Math.ceil(fileData.size / (1024 * 1024)), // 1MB chunks
                fileId: fileId,
                folderId: folder || null,
                uploadEndpoint,
              })
            );

            // Store file reference in upload service
            uploadService.storeFile(fileId, fileData._fileObject);
          } catch (error) {
            console.error("Error initializing upload:", error);
            toast.error(`Failed to initialize upload for ${fileData.name}`);
          }
        }
      });

      // Process upload queue - this will trigger the upload panel
      uploadService.processUploadQueue();
    },
    [limit, files, accept, onChange, dispatch, folder, useMasterApi]
  );

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      const localFiles = _formatFiles(e.dataTransfer.files);

      // Use the same validation logic as handleFileChange
      const validFiles = localFiles.filter((file) => {
        const extension = file.name.split(".").pop().toLowerCase();
        const isValidExtension = _allExtensions.includes(extension);
        const isAcceptedFormat = !accept.length || accept.includes(extension);

        if (!isValidExtension || !isAcceptedFormat) {
          file.loading = false;
          file.error = true;
          return false;
        }
        return true;
      });

      // Calculate new total based on whether multiple files are allowed
      const existingCount = multiple ? files.length : 0;
      if (existingCount + validFiles.length > limit) {
        toast.error(`You can only upload up to ${limit} files.`);
        return;
      }

      if (!validFiles?.length) {
        if (accept?.length) {
          toast.error(
            `Please check the file format! Only the ${accept.join(
              ", "
            )} formats are allowed.`
          );
        } else {
          toast.error("Please check the file format!");
        }
        return;
      }

      // Add/replace files in local state immediately
      const newFiles = multiple
        ? [...files, ...validFiles]
        : validFiles.slice(0, limit); // single-file mode: replace old with new
      setFiles(newFiles);
      onChange(newFiles);

      // Start upload process for each file (same as handleFileChange)
      const relativeEndpoint = "/service/upload-chunk";
      const uploadEndpoint =
        useMasterApi && MASTER_API_URL
          ? `${MASTER_API_URL.replace(/\/+$/, "")}${relativeEndpoint}`
          : relativeEndpoint;

      validFiles.forEach((fileData) => {
        if (fileData._fileObject) {
          try {
            const fileId = fileData.uuid;

            // Initialize upload in Redux
            dispatch(
              initializeUpload({
                file: {
                  name: fileData.name,
                  size: fileData.size,
                  type: fileData.type,
                  lastModified: fileData.lastModified,
                },
                totalChunks: Math.ceil(fileData.size / (1024 * 1024)),
                fileId: fileId,
                folderId: folder || null,
                uploadEndpoint,
              })
            );

            // Store file reference in upload service
            uploadService.storeFile(fileId, fileData._fileObject);
          } catch (error) {
            console.error("Error initializing upload:", error);
            toast.error(`Failed to initialize upload for ${fileData.name}`);
          }
        }
      });

      // Process upload queue - this will trigger the upload panel
      uploadService.processUploadQueue();
    },
    [files, accept, limit, onChange, dispatch, folder, useMasterApi]
  );

  const removeFile = (uuid) => {
    const updatedFiles = files.filter((file) => file.uuid !== uuid);
    setFiles(updatedFiles);
    onChange(updatedFiles);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  if (hidden) return null;

  return component ? (
    <div
      className={`  text-center  cursor-pointer`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleButtonClick}
    >
      <input
        type="file"
        id="fileInput"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileChange}
        placeholder={placeholder}
        multiple={multiple}
        accept={accept.join(",")}
      />

      <div className="cursor-pointer text-left flex flex-row gap-4 items-center p-4">
        <div className="bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] w-12 h-12 flex-center text-white rounded-md">
         {loading ? (
           <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
         ) : (
           component ? component : <GrAttachment />
         )}
        </div>
      </div>
    </div>
  ) : (
    <div className="flex-col gap-1 relative flex">
      {label && (
        <div className="block">
          <label
            className={`text-theme font-medium text-sm ${labelClass}`}
            htmlFor={id}
          >
            {label}
            {required && <span className="text-red-600">*</span>}
          </label>
        </div>
      )}

      <div
        className={`border-2 border-dashed rounded-lg text-center w-full cursor-pointer ${
          isDragging ? "border-theme bg-blue-50" : "border-theme bg-gray-100"
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleButtonClick}
      >
        <input
          type="file"
          id="fileInput"
          ref={fileInputRef}
          className="hidden"
          onChange={handleFileChange}
          placeholder={placeholder}
          multiple={multiple}
          accept={accept.join(",")}
        />

        {files?.length ? (
          <div className="cursor-pointer text-left items-start flex flex-row gap-4 p-4">
            <div className="bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] w-12 h-12 flex-center text-white rounded-md">
              {loading ? (
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
              ) : (
                <GrAttachment />
              )}
            </div>
            <div className="w-full text-sm flex flex-col">
              <span className="text-theme text-left text-sm font-bold">
                Attachments ({files?.length})
              </span>
              {files?.map((file) => (
                <div
                  key={file?.uuid}
                  className="flex flex-row justify-between items-center gap-6"
                >
                  <div className="flex flex-row justify-between gap-2">
                    <span className="flex flex-row gap-2 items-center text-theme">
                      <GrAttachment />
                      <span className="truncate w-32">{file?.name}</span>
                    </span>
                    <span className="text-sm text-theme">
                      {_formatFileSize(file?.size)}
                    </span>
                  </div>
                  <span className="flex flex-row">
                    {file?.loading && (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-theme border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-xs text-theme">
                          {file?.progress || 0}%
                        </span>
                      </div>
                    )}
                    {file?.error ? (
                      <span className="text-red-500">
                        <MdError />
                      </span>
                    ) : null}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        removeFile(file?.uuid);
                      }}
                      className="text-red-500 hover:text-red-600"
                    >
                      <RxCross2 />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="cursor-pointer text-left flex flex-row gap-4 items-center p-4">
            <div className="bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] w-12 h-12 flex-center text-white rounded-md">
              {loading ? (
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
              ) : (
                <GrAttachment />
              )}
            </div>
            <div className="text-sm">
              <p className="text-theme">Drag and drop a file here.</p>
              <span className="text-textLight font-normal leading-none">
                {info}
              </span>
            </div>
          </div>
        )}
      </div>

      {error ? (
        <span className="flex items-center gap-1 text-sm text-error-600">
          <MdError />
          <p>{error}</p>
        </span>
      ) : null}
    </div>
  );
};

export default ReduxFileUploader;
